import { useFormState, useWatch } from 'react-hook-form';
import {
    FormInputField,
    FormSwitchField,
    FormTextareaField,
    LocalizedPropertyObject,
    LocalizedText,
    FormControls,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Divider, Stack, Typography } from '@optimus-web/core';
import { SelectField } from '@mews-commander/form';
import { getLanguageOptions } from '@mews-commander/legacy-entities/src/ducks/languages';
import { useCommanderSelector } from '@mews-commander/utils';
import { OnlineCheckInFormValues } from './types';

interface OnlineCheckInFormFieldsProps extends Pick<FormControls<OnlineCheckInFormValues>, 'submit'> {
    messageTemplate: LocalizedPropertyObject | undefined;
    areSeparateSMSTypesEnabled: boolean;
}

export const OnlineCheckInFormFields = ({
    messageTemplate,
    areSeparateSMSTypesEnabled,
    submit,
}: OnlineCheckInFormFieldsProps) => {
    const { dirtyFields } = useFormState();
    const languageCode = useWatch<OnlineCheckInFormValues, 'languageCode'>({ name: 'languageCode' });
    const translatedTemplate = messageTemplate?.[languageCode] ?? '';
    const languageOptions = useCommanderSelector(getLanguageOptions);
    const isDirty = dirtyFields.isEnabled != null;

    return (
        <Stack vertical spacing="200">
            {areSeparateSMSTypesEnabled && (
                <Stack vertical spacing="200">
                    <Stack itemAlignment="center" distribution="space-between">
                        <Typography textStyle="bodyLargeStrong" as="p">
                            <LocalizedText textKey={TextKey.AfterStartTabSwitchFieldLabel} />
                        </Typography>
                        <FormSwitchField label={null} name="isEnabled" />
                    </Stack>
                    <Divider />
                </Stack>
            )}
            <SelectField
                label={<LocalizedText textKey={TextKey.Language} />}
                options={languageOptions}
                name="languageCode"
                showEmptyValue={false}
                size="large"
            />
            {!areSeparateSMSTypesEnabled && (
                <>
                    <FormInputField
                        label={<LocalizedText textKey={TextKey.SenderName} />}
                        name="senderName"
                        size="large"
                        disabled
                    />
                    <FormInputField
                        label={<LocalizedText textKey={TextKey.SenderPhoneNumberShort} />}
                        name="senderPhoneNumber"
                        size="large"
                        disabled
                    />
                </>
            )}
            <FormTextareaField
                label={<LocalizedText textKey={TextKey.Message} />}
                name="message"
                rows={2}
                size="large"
                disabled={!areSeparateSMSTypesEnabled}
                readOnly={areSeparateSMSTypesEnabled}
                placeholder={translatedTemplate}
            />

            {areSeparateSMSTypesEnabled && (
                <Stack alignment="end">
                    <Button disabled={!isDirty} variant="primary" onClick={submit} size="medium" type="submit">
                        <LocalizedText textKey={TextKey.Save} />
                    </Button>
                </Stack>
            )}
        </Stack>
    );
};
